/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { normalizeProductCategoryId } from '../../../catalog/domain-api'
import type { Product } from '../../../catalog/domain-api';
import type { PosCatalogItemTypeFilter } from '../../domain/posTypes';
import { ProminentSearchWithScanner } from '../../../shared/ui/ProminentSearchWithScanner';
import { PosCatalogStockFiltersPanel } from '../../../shared/filters/PosCatalogStockFiltersPanel'
import { OperationalFiltersClearRow } from '../../../shared/filters/OperationalFiltersClearRow'
import { useOperationalFilterLayout } from '../../../shared/filters/useOperationalFilterLayout'
import { FilterSheetButton } from '../../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../../shared/filters/FilterSheetModal';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { styles } from '../posShared.styles';
import type { CategoryOption } from '../../../catalog/domain-api';
import { useWebPosWriteGate } from '../../application/useWebPosWriteGate';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { MIN_PRODUCT_SEARCH_QUERY_CHARS } from '../../../shared/product-search/productSearchUx';
import { useOperationalWebSearchFocus } from '../../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { TicketMoreActionsSheet } from '../../../shared/ticket/TicketMoreActionsSheet'
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader'
import { ticketOperationalHeader as oh } from '../../../shared/ticket/ticketOperationalHeader.styles';
import { PosCashStatusLine } from './PosCashStatusLine';
import { OperationalFilterSegmentedRow } from '../../../shared/filters/OperationalFilterPrimitives';
import { PosQuickAccessRail, type PosQuickAccessRailProps } from './PosQuickAccessRail';
import { PosCatalogActiveFiltersRow } from './PosCatalogActiveFiltersRow';
import {
  getPosFulfillmentType,
  posFulfillmentHeaderTitle,
  setPosFulfillmentType,
  type PosFulfillmentType,
} from '../../../shared/pos/posHandoffSettings';
import {
  listPosCatalogActiveFilterChips,
  type PosActiveCatalogFiltersSummaryParams,
} from '../../application/posActiveCatalogFiltersSummary';
import { POS_SEARCH_BARCODE_ACCESSIBILITY_HINT } from '../../application/posLayout.constants';
import {
  posDeskCatalogChromeVisible,
  posQuickAccessRailShowChipRow,
  posRailShowsCategoryControl,
} from '../../application/posQuickAccessRail';

export type PosHeaderSectionProps = {
  canEdit: boolean;
  /** QSR: toggle Para llevar / Comer acá en lugar del título fijo Mostrador. */
  showFulfillmentToggle?: boolean;
  /** Mesa: sin toggle; título fijo Comer acá. */
  fulfillmentLockedTo?: PosFulfillmentType | null;
  trialBanner?: React.ReactNode;
  operationalAlertsBand?: React.ReactNode;
  activeCashSessionId: string | null;
  cashSessionLoading: boolean;
  /** Caja ya resuelta para la sucursal activa — evita flash "Verificando…" en revalidaciones. */
  cashSessionReady?: boolean;
  cashSessionOpenedAt?: string | null;
  cashSessionOpeningAmount?: number | null;
  selectedCategory: Product['categoryId'] | 'all';
  itemTypeFilter: PosCatalogItemTypeFilter;
  categories: CategoryOption[];
  searchQuery: string;
  lookupFeedback: string | null;
  onSetCategory: (category: Product['categoryId'] | 'all') => void;
  onSetItemTypeFilter: (value: PosCatalogItemTypeFilter) => void;
  onSetSearchQuery: (value: string) => void;
  onOpenCategorySelector: () => void;
  onOpenScanner: () => void;
  onOpenVarious: () => void;
  onOpenRecarga?: () => void;
  /** Cap `misc_quick_sale`. Default true para no romper callers legacy. */
  allowsMiscQuickSale?: boolean;
  showBarcodeCameraButton?: boolean;
  onBarcodeSearchSubmit?: () => void;
  autoFocusSearchOnWeb?: boolean;
  onOpenCashManagement?: () => void;
  onOpenSalesHistory?: () => void;
  onOpenPromotions?: () => void;
  posPhysicalStockOnly?: boolean;
  stockOnlyFilterDisabled?: boolean;
  onTogglePosPhysicalStockOnly?: () => void;
  posBrandFilter?: string | null;
  onPosBrandFilterChange?: (value: string | null) => void;
  posBrandOptions?: Array<{ id: string; name: string }>;
  posSupplierFilter?: string | null;
  onPosSupplierFilterChange?: (value: string | null) => void;
  posSupplierOptions?: Array<{ id: string; name: string }>;
  hasActiveCatalogFilters?: boolean;
  onClearCatalogFilters?: () => void;
  /** Rail de acceso rápido (Favoritos/Combos/Oferta/Paquetes + categorías). */
  rail?: PosQuickAccessRailProps | null;
  /**
   * Desk: `toolbar` = Cobrar + buscador (ancho total); `catalogChrome` = Para llevar + rail (solo catálogo).
   * Mobile / omitido = bloque completo.
   */
  deskPart?: 'full' | 'toolbar' | 'catalogChrome';
};

function PosHeaderSectionInner({
  canEdit,
  showFulfillmentToggle = false,
  fulfillmentLockedTo = null,
  trialBanner,
  operationalAlertsBand,
  activeCashSessionId,
  cashSessionLoading,
  cashSessionReady = false,
  cashSessionOpenedAt = null,
  cashSessionOpeningAmount = null,
  selectedCategory,
  itemTypeFilter = 'all',
  categories,
  searchQuery,
  lookupFeedback,
  onSetCategory,
  onSetItemTypeFilter,
  onSetSearchQuery,
  onOpenCategorySelector,
  onOpenScanner,
  onOpenVarious,
  onOpenRecarga,
  allowsMiscQuickSale = true,
  showBarcodeCameraButton = true,
  onBarcodeSearchSubmit,
  autoFocusSearchOnWeb = false,
  onOpenCashManagement,
  onOpenSalesHistory,
  onOpenPromotions,
  posPhysicalStockOnly = false,
  stockOnlyFilterDisabled = false,
  onTogglePosPhysicalStockOnly,
  posBrandFilter = null,
  onPosBrandFilterChange,
  posBrandOptions = [],
  posSupplierFilter = null,
  onPosSupplierFilterChange,
  posSupplierOptions = [],
  hasActiveCatalogFilters = false,
  onClearCatalogFilters,
  rail = null,
  deskPart = 'full',
}: PosHeaderSectionProps) {
  const { layoutWide } = useOperationalFilterLayout();
  const { offlineHint } = useWebPosWriteGate();
  const hasServicePackages = useHasBusinessCapability('service_packages');
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const searchInputRef = useRef<TextInput>(null);
  const [moreActionsSheetOpen, setMoreActionsSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [fulfillmentType, setFulfillmentTypeLocal] = useState<PosFulfillmentType>(getPosFulfillmentType);
  useEffect(() => {
    if (fulfillmentLockedTo) {
      setPosFulfillmentType(fulfillmentLockedTo);
      setFulfillmentTypeLocal(fulfillmentLockedTo);
    }
  }, [fulfillmentLockedTo]);
  // Con toggle de fulfillment abajo, el título no repite el modo (queda "Mostrador").
  const headerTitle = fulfillmentLockedTo
    ? posFulfillmentHeaderTitle(fulfillmentLockedTo)
    : 'Mostrador';
  const onFulfillmentChange = (value: PosFulfillmentType) => {
    setFulfillmentTypeLocal(value);
    setPosFulfillmentType(value);
  };
  useOperationalWebSearchFocus(searchInputRef, {
    shortcutsEnabled: canEdit,
  });

  const searchMinCharsHint = useMemo(() => {
    const q = searchQuery.trim();
    if (q.length === 0 || q.length >= MIN_PRODUCT_SEARCH_QUERY_CHARS) return null;
    return `Escribí al menos ${MIN_PRODUCT_SEARCH_QUERY_CHARS} letras. El código completo lo buscás con Enter o SCAN.`;
  }, [searchQuery]);

  const searchFooter = useMemo(() => {
    if (!searchMinCharsHint && !lookupFeedback) return null;
    return (
      <>
        {searchMinCharsHint ? <Text style={styles.lookupFeedback}>{searchMinCharsHint}</Text> : null}
        {lookupFeedback ? <Text style={styles.lookupFeedback}>{lookupFeedback}</Text> : null}
      </>
    );
  }, [lookupFeedback, searchMinCharsHint]);

  const searchPlaceholder = 'Buscar nombre, SKU, código…';
  const searchPlaceholderCompact = searchPlaceholder;

  const categoryPickerLabel = useMemo(() => {
    if (selectedCategory === 'all') return 'Todas';
    const opt = categories.find(
      (c) => normalizeProductCategoryId(c.id) === normalizeProductCategoryId(selectedCategory),
    );
    return opt?.label ?? String(selectedCategory);
  }, [categories, selectedCategory]);

  const filtersMetaRow =
    hasActiveCatalogFilters && onClearCatalogFilters ? (
      <OperationalFiltersClearRow
        hasActiveFilters={hasActiveCatalogFilters}
        onClearFilters={onClearCatalogFilters}
      />
    ) : null;

  const cashClosedMessage = 'Para cobrar en efectivo, abrí el turno.';

  const showCashVerifying = cashSessionLoading && !cashSessionReady;

  const panelActiveFilterCount = useMemo(() => {
    let count = 0;
    if (itemTypeFilter != null && itemTypeFilter !== 'all') count += 1;
    if (selectedCategory != null && selectedCategory !== 'all') count += 1;
    if (posPhysicalStockOnly) count += 1;
    if (posBrandFilter) count += 1;
    if (posSupplierFilter) count += 1;
    return count;
  }, [itemTypeFilter, posBrandFilter, posPhysicalStockOnly, posSupplierFilter, selectedCategory]);

  const catalogFilterSummaryParams = useMemo(
    (): PosActiveCatalogFiltersSummaryParams => ({
      selectedCategory,
      categoryLabel: categoryPickerLabel,
      itemTypeFilter,
      posPhysicalStockOnly,
      posBrandFilter: posBrandFilter ?? null,
      posBrandName: posBrandOptions.find((b) => b.id === posBrandFilter)?.name ?? null,
      posSupplierFilter: posSupplierFilter ?? null,
      posSupplierName: posSupplierOptions.find((s) => s.id === posSupplierFilter)?.name ?? null,
    }),
    [
      categoryPickerLabel,
      itemTypeFilter,
      posBrandFilter,
      posBrandOptions,
      posPhysicalStockOnly,
      posSupplierFilter,
      posSupplierOptions,
      selectedCategory,
    ],
  );

  const activeCatalogFilterChips = useMemo(() => {
    const listed = listPosCatalogActiveFilterChips(catalogFilterSummaryParams);
    return listed.map((chip) => {
      const onDismiss = () => {
        switch (chip.key) {
          case 'category':
            onSetCategory('all');
            break;
          case 'itemType':
            onSetItemTypeFilter('all');
            break;
          case 'stock':
            onTogglePosPhysicalStockOnly?.();
            break;
          case 'brand':
            onPosBrandFilterChange?.(null);
            break;
          case 'supplier':
            onPosSupplierFilterChange?.(null);
            break;
          default:
            break;
        }
      };
      return { ...chip, onDismiss };
    });
  }, [
    catalogFilterSummaryParams,
    onPosBrandFilterChange,
    onPosSupplierFilterChange,
    onSetCategory,
    onSetItemTypeFilter,
    onTogglePosPhysicalStockOnly,
  ]);

  const deskFiltersButtonLabel = useMemo(() => {
    if (activeCatalogFilterChips.length === 1) {
      return `Filtros · ${activeCatalogFilterChips[0].label}`;
    }
    if (panelActiveFilterCount > 0) {
      return `Filtros · ${panelActiveFilterCount}`;
    }
    return undefined;
  }, [activeCatalogFilterChips, panelActiveFilterCount]);

  const activeCatalogFiltersRow =
    activeCatalogFilterChips.length > 0 && onClearCatalogFilters ? (
      <View style={styles.posActiveFiltersRow}>
        <PosCatalogActiveFiltersRow
          chips={activeCatalogFilterChips}
          onClearAll={onClearCatalogFilters}
        />
      </View>
    ) : null;

  const showCashStatusLine =
    showCashVerifying || !activeCashSessionId;

  const sheetActions = useMemo(
    () =>
      [
        canEdit && allowsMiscQuickSale
          ? {
              key: 'varios',
              label: 'Venta rápida: Varios',
              hint: 'Un monto suelto, sin producto del catálogo.',
              icon: 'flash-outline' as const,
              onPress: onOpenVarious,
              disabled: false,
            }
          : null,
        canEdit && onOpenRecarga
          ? {
              key: 'recarga',
              label: 'Recarga SUBE / virtual',
              hint: 'No es del catálogo. Si cobrás en efectivo, entra a la caja.',
              icon: 'phone-portrait-outline' as const,
              onPress: onOpenRecarga,
              disabled: false,
            }
          : null,
        onOpenCashManagement
          ? {
              key: 'cash',
              label: 'Caja',
              hint: 'Abrí, cerrá o sacá plata.',
              icon: 'wallet-outline' as const,
              onPress: onOpenCashManagement,
              disabled: false,
            }
          : null,
        onOpenSalesHistory
          ? {
              key: 'salesHistory',
              label: 'Historial de ventas',
              hint: 'Las de hoy y las de antes.',
              icon: 'receipt-outline' as const,
              onPress: onOpenSalesHistory,
              disabled: false,
            }
          : null,
        onOpenPromotions
          ? {
              key: 'promotions',
              label: 'Promociones',
              hint: 'Descuentos y ofertas de ahora.',
              icon: 'pricetag-outline' as const,
              onPress: onOpenPromotions,
              disabled: false,
            }
          : null,
      ].filter((item): item is NonNullable<typeof item> => item != null),
    [
      allowsMiscQuickSale,
      canEdit,
      onOpenCashManagement,
      onOpenPromotions,
      onOpenRecarga,
      onOpenSalesHistory,
      onOpenVarious,
    ]
  );

  const moreActionsModal = (
    <TicketMoreActionsSheet
      visible={moreActionsSheetOpen}
      title="Más acciones"
      actions={sheetActions}
      onClose={() => setMoreActionsSheetOpen(false)}
    />
  );

  const filtersSheet = (
    <FilterSheetModal
      visible={filterSheetOpen}
      onClose={() => setFilterSheetOpen(false)}
      onApply={() => setFilterSheetOpen(false)}
      onClear={onClearCatalogFilters ?? (() => {})}
      hasActiveDraft={hasActiveCatalogFilters}
      appearance="ticket"
    >
      <PosCatalogStockFiltersPanel
        mode="pos"
        sheetMode
        panelTitle="Filtros"
        metaRow={filtersMetaRow}
        collapsible={false}
        panelActiveFilterCount={panelActiveFilterCount}
        itemTypeFilter={itemTypeFilter}
        onItemTypeFilterChange={onSetItemTypeFilter}
        showPackagesOption={hasServicePackages}
        categoryLabel={categoryPickerLabel}
        categoryActive={selectedCategory !== 'all'}
        categoryFilters={categories}
        categoryFilterId={selectedCategory}
        onCategoryFilterChange={(id) => {
          onSetCategory(id == null || id === 'all' ? 'all' : id);
        }}
        brandFilter={posBrandFilter}
        onBrandFilterChange={onPosBrandFilterChange}
        brandOptions={posBrandOptions}
        supplierFilter={posSupplierFilter}
        onSupplierFilterChange={onPosSupplierFilterChange}
        supplierOptions={posSupplierOptions}
        physicalStockOnly={posPhysicalStockOnly}
        stockOnlyFilterDisabled={stockOnlyFilterDisabled}
        onTogglePhysicalStockOnly={
          inventoryStockUxEnabled ? onTogglePosPhysicalStockOnly : undefined
        }
        showStockAvailabilityFilters={inventoryStockUxEnabled}
        stockOnlyHint="Elegí una sucursal para filtrar por stock."
      />
    </FilterSheetModal>
  );

  const deskMoreActions = useMemo(
    () => sheetActions.filter((action) => action.key !== 'varios' && action.key !== 'recarga'),
    [sheetActions]
  );

  const showDeskCatalogChromeBlock = useMemo(
    () =>
      posDeskCatalogChromeVisible({
        showFulfillmentToggle,
        layoutWide,
        railCounts: rail?.counts,
        categoryOptions: categories,
        hasCategoryPicker: onOpenCategorySelector != null,
      }),
    [categories, layoutWide, onOpenCategorySelector, rail?.counts, showFulfillmentToggle],
  );

  const showRailChipRow = useMemo(() => {
    if (!rail) return false;
    const leafCount = categories.filter((c) => {
      if (c.id === 'all') return false;
      if (rail.counts.combos > 0 && c.label.trim().toLowerCase() === 'combos') return false;
      return true;
    }).length;
    const showCategoryOnRail =
      onOpenCategorySelector != null && posRailShowsCategoryControl(layoutWide, leafCount);
    return posQuickAccessRailShowChipRow(layoutWide, rail.counts, showCategoryOnRail);
  }, [categories, layoutWide, onOpenCategorySelector, rail]);

  const deskCatalogChromeRenders =
    showDeskCatalogChromeBlock && (showFulfillmentToggle || showRailChipRow);

  const deskCatalogChrome = deskCatalogChromeRenders ? (
    <View style={styles.posDeskCatalogChrome}>
      {showFulfillmentToggle ? (
        <View style={[styles.posFulfillmentInHeader, styles.posFulfillmentInHeaderDeskCatalog]}>
          <OperationalFilterSegmentedRow
            ticketTone
            testIDPrefix="pos.fulfillment"
            value={fulfillmentType}
            onChange={onFulfillmentChange}
            options={[
              { value: 'pickup', label: 'Para llevar', accessibilityLabel: 'Para llevar' },
              { value: 'dine_in', label: 'Comer acá', accessibilityLabel: 'Comer acá' },
            ]}
          />
        </View>
      ) : null}
      {rail && showRailChipRow ? (
        <View
          style={[
            styles.posQuickRailSlot,
            styles.posQuickRailSlotDeskCatalog,
            showFulfillmentToggle && styles.posQuickRailSlotAfterFulfillmentDesk,
          ]}
        >
          <PosQuickAccessRail {...rail} embedded />
        </View>
      ) : null}
    </View>
  ) : null;

  // ── Wide (PC): desk toolbar — search + Filtros · Más · Varios ──
  if (layoutWide && deskPart === 'catalogChrome') {
    return deskCatalogChrome;
  }

  if (layoutWide) {
    const deskActions = (
      <View style={oh.deskActionRail}>
        <FilterSheetButton
          activeCount={panelActiveFilterCount}
          onOpen={() => setFilterSheetOpen(true)}
          variant="desk"
          deskLabel={deskFiltersButtonLabel}
          deskActive={panelActiveFilterCount > 0}
          testID="pos.filters.button"
        />
        {deskMoreActions.length > 0 ? (
          <TouchableOpacity
            style={oh.deskBtn}
            onPress={() => setMoreActionsSheetOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Más acciones de POS"
            accessibilityHint="Caja, historial de ventas, promociones"
          >
            <Text style={oh.deskBtnText}>Más</Text>
          </TouchableOpacity>
        ) : null}
        {canEdit && allowsMiscQuickSale ? (
          <TouchableOpacity
            style={oh.deskBtnGold}
            onPress={onOpenVarious}
            accessibilityRole="button"
            accessibilityLabel="Venta rápida, varios"
          >
            <Text style={oh.deskBtnGoldText}>Varios</Text>
          </TouchableOpacity>
        ) : null}
        {canEdit && onOpenRecarga ? (
          <TouchableOpacity
            style={oh.deskBtn}
            onPress={onOpenRecarga}
            accessibilityRole="button"
            accessibilityLabel="Registrar recarga SUBE o virtual"
          >
            <Text style={oh.deskBtnText}>Recarga</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );

    return (
      <View style={styles.headerWide}>
        {operationalAlertsBand}
        {trialBanner}
        {!offlineHint ? null : (
          <Text style={styles.posOfflineHint} numberOfLines={2}>
            {offlineHint}
          </Text>
        )}
        {!canEdit ? (
          <View style={modalExperience.warningPanel}>
            <Text style={styles.readOnlyText}>Modo solo lectura. Podés consultar ítems y carrito, sin cobrar.</Text>
          </View>
        ) : null}

        <TicketModuleHeader
          kicker="Cobrar"
          title={headerTitle}
          style={styles.posDeskHeaderChromeSheet}
          padStyle={
            deskCatalogChromeRenders
              ? styles.posDeskCatalogToolbarPadSplit
              : styles.posDeskCatalogToolbarPad
          }
          tools={
            <View style={posDeskStyles.toolbarRow}>
              <View style={posDeskStyles.searchField}>
                <ProminentSearchWithScanner
                  inputRef={searchInputRef}
                  value={searchQuery}
                  onChangeText={onSetSearchQuery}
                  onPressScan={onOpenScanner}
                  showBarcodeCameraButton={showBarcodeCameraButton}
                  placeholder={searchPlaceholder}
                  placeholderCompact={searchPlaceholderCompact}
                  compactTitleBreakpoint={400}
                  sectionLabel={null}
                  accessibilityLabel="Buscar producto o servicio"
                  accessibilityHint={POS_SEARCH_BARCODE_ACCESSIBILITY_HINT}
                  showHidHint={false}
                  onSubmitEditing={onBarcodeSearchSubmit}
                  disabled={!canEdit}
                  scanTone="neutral"
                  unifiedField
                  unifiedBoxStyle={oh.deskSearchBox}
                  inputStyle={oh.deskSearchInput}
                  footer={searchFooter}
                  textInputProps={{ testID: 'pos.search.input' }}
                />
              </View>
              {deskActions}
            </View>
          }
        >
          {activeCatalogFiltersRow}
          {deskPart === 'full' ? deskCatalogChrome : null}
        </TicketModuleHeader>

        {showCashStatusLine ? (
          <View style={styles.headerWideCash}>
            <PosCashStatusLine
              showCashVerifying={showCashVerifying}
              activeCashSessionId={activeCashSessionId}
              cashClosedMessage={cashClosedMessage}
              onOpenCashManagement={onOpenCashManagement}
              cashSessionOpenedAt={cashSessionOpenedAt}
              cashSessionOpeningAmount={cashSessionOpeningAmount}
            />
          </View>
        ) : null}

        {filtersSheet}
        <TicketMoreActionsSheet
          visible={moreActionsSheetOpen}
          title="Más acciones"
          actions={deskMoreActions}
          onClose={() => setMoreActionsSheetOpen(false)}
        />
      </View>
    );
  }

  // ── Mobile: ticket line ──
  return (
    <View style={[styles.header, styles.headerMobileCompact]}>
      {operationalAlertsBand}
      {trialBanner}
      {!offlineHint ? null : (
        <Text style={styles.posOfflineHint} numberOfLines={2}>
          {offlineHint}
        </Text>
      )}

      {!canEdit ? (
        <View style={modalExperience.warningPanel}>
          <Text style={styles.readOnlyText}>Modo solo lectura. Podés consultar ítems y carrito, sin cobrar.</Text>
        </View>
      ) : null}

      <TicketModuleHeader
        kicker="Cobrar"
        title={headerTitle}
        tools={
          <>
            <ProminentSearchWithScanner
              inputRef={searchInputRef}
              value={searchQuery}
              onChangeText={onSetSearchQuery}
              onPressScan={onOpenScanner}
              showBarcodeCameraButton={showBarcodeCameraButton}
              placeholder={searchPlaceholder}
              placeholderCompact={searchPlaceholderCompact}
              compactTitleBreakpoint={400}
              sectionLabel={null}
              accessibilityLabel="Buscar producto o servicio"
              accessibilityHint={POS_SEARCH_BARCODE_ACCESSIBILITY_HINT}
              showHidHint={false}
              onSubmitEditing={onBarcodeSearchSubmit}
              disabled={!canEdit}
              scanTone="neutral"
              scanIconOnlyBreakpoint={999}
              showSearchIcon
              footer={searchFooter}
              textInputProps={{ testID: 'pos.search.input' }}
            />

            <View style={posMobileStyles.toolRowMobile}>
              <FilterSheetButton
                activeCount={panelActiveFilterCount}
                onOpen={() => setFilterSheetOpen(true)}
                style={posMobileStyles.filtersInToolRow}
                variant="ticket"
              />
              {sheetActions.length > 0 ? (
                <TouchableOpacity
                  style={posMobileStyles.actionMoreMobile}
                  onPress={() => setMoreActionsSheetOpen(true)}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Más acciones de POS"
                  accessibilityHint="Venta rápida, recargas, caja, historial de ventas, promociones"
                >
                  <Ionicons name="ellipsis-horizontal" size={20} color={ticketColors.ink} />
                </TouchableOpacity>
              ) : null}
            </View>
          </>
        }
      >
        {activeCatalogFiltersRow}
        {showFulfillmentToggle ? (
          <View
            style={[
              styles.posFulfillmentInHeader,
              styles.posFulfillmentInHeaderMobile,
              !showRailChipRow && styles.posFulfillmentMobileRailToGridGap,
            ]}
          >
            <OperationalFilterSegmentedRow
              ticketTone
              layout="stretch"
              testIDPrefix="pos.fulfillment"
              value={fulfillmentType}
              onChange={onFulfillmentChange}
              options={[
                { value: 'pickup', label: 'Para llevar', accessibilityLabel: 'Para llevar' },
                { value: 'dine_in', label: 'Comer acá', accessibilityLabel: 'Comer acá' },
              ]}
            />
          </View>
        ) : null}
        {rail && showRailChipRow ? (
          <View style={[styles.posQuickRailSlot, styles.posQuickRailSlotMobile]}>
            <PosQuickAccessRail {...rail} embedded />
          </View>
        ) : null}
      </TicketModuleHeader>

      {showCashStatusLine ? (
        <PosCashStatusLine
          showCashVerifying={showCashVerifying}
          activeCashSessionId={activeCashSessionId}
          cashClosedMessage={cashClosedMessage}
          onOpenCashManagement={onOpenCashManagement}
          cashSessionOpenedAt={cashSessionOpenedAt}
          cashSessionOpeningAmount={cashSessionOpeningAmount}
        />
      ) : null}

      {filtersSheet}
      {moreActionsModal}
    </View>
  );
}

export const PosHeaderSection = memo(PosHeaderSectionInner);

const posDeskStyles = StyleSheet.create({
  toolbarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  searchField: {
    flex: 1,
    minWidth: 200,
  },
});

const posMobileStyles = StyleSheet.create({
  toolRowMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: 8,
  },
  filtersInToolRow: {
    flex: 1,
    marginBottom: 0,
    minWidth: 0,
  },
  actionMoreMobile: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});
